import {ChangeDetectorRef, Component, EventEmitter, Input, OnInit, Output} from '@angular/core';
import {
    Permission,
    PermissionAssignment,
    PermissionObjectType,
    PermissionSubjectType,
    User
} from 'smag-commons/dto/api';
import {PermissionsService} from '../../../shared/permissions.service';
import {Observable, of} from 'rxjs';
import {UsersService} from '../../../shared/users.service';
import {first, map, switchMap} from 'rxjs/operators';
import {can} from '../../../shared/permissions';
import {UserFilter} from 'auth-commons';
import {DEFAULT_ORDER} from '../users-permission-assignments/users-permission-assignments.component';
import {PermissionSettings} from '../permission-list/permission-list.component';

export interface PermissionAssignmentObject {
    type: PermissionObjectType,
    id?: string
    permissionSettings: PermissionSettings;
    hint?: string;
}

const SELECTED_USER_COLOR = 'lightblue';

@Component({
    selector: 'app-new-permission-assignment',
    templateUrl: './new-permission-assignment.component.html',
    styleUrls: ['./new-permission-assignment.component.scss']
})
export class NewPermissionAssignmentComponent implements OnInit {

    @Input() subjectType: PermissionSubjectType;
    @Input() object: PermissionAssignmentObject;
    @Output() assignment: EventEmitter<PermissionAssignment> = new EventEmitter<PermissionAssignment>();
    @Output() valid: EventEmitter<boolean> = new EventEmitter<boolean>();
    @Output() dirty: EventEmitter<boolean> = new EventEmitter<boolean>();

    userFilter: UserFilter;
    user?: User;
    assignments: PermissionAssignment[];
    objectAssignment: PermissionAssignment;
    permissions: Permission[];
    canAssign: boolean;

    // exported in HTML
    PermissionSubjectType = PermissionSubjectType;
    SELECTED_USER_COLOR = SELECTED_USER_COLOR;

    constructor(private readonly changeDetectorRef: ChangeDetectorRef,
                private readonly permissionsService: PermissionsService,
                private readonly usersService: UsersService) {
        this.userFilter = {
            username: null,
            firstName: null,
            lastName: null,
            enabled: null,
            missingChecks: [],
            missingChecksOr: [],
            enabled2FA: null,
            mandatory2FA: null,
            passwordValid: null,
            orderBy: [DEFAULT_ORDER]
        };
        this.user = null;
        this.assignments = null;
        this.objectAssignment = null;
        this.permissions = [];
        this.canAssign = false;
    }

    ngOnInit() {
    }

    onSelectUser(user: User) {
        this.user = user;
        this.loadSubjectAssignments(PermissionSubjectType.USER, user.u3id);
    }

    onChangePermissions(subjectType: PermissionSubjectType, subjectId: string, permissions: Permission[]) {
        this.permissions = [...permissions];
        this.updateAssignment(subjectType, subjectId);
        this.valid.emit(true);
        this.dirty.emit(true);
        // let PermissionListComponent discover the change
        this.changeDetectorRef.markForCheck();
    }

    onClickClearPermissions(subjectType: PermissionSubjectType, subjectId: string) {
        this.permissions = [];
        this.updateAssignment(subjectType, subjectId);
        this.valid.emit(true);
        this.dirty.emit(true);
        // let PermissionListComponent discover the change
        this.changeDetectorRef.markForCheck();
    }

    private loadSubjectAssignments(subjectType: PermissionSubjectType, subjectId: string) {
        // NOTE: NewPermissionAssignmentComponent assumes that PERMISSIONS permission has already been checked for
        //       the referenced object, and thus does not attempt further checks in this respect;
        //       however, the subject must still be checked
        this.assignments = null; // trigger loading spinner
        this.canAssignSubject(subjectType, subjectId).pipe(
            switchMap(canAssign => this.permissionsService.getPermissionsForSubject(subjectType, subjectId).pipe(
                map(assignments => [assignments, canAssign] as [PermissionAssignment[], boolean])
            ))
        ).subscribe(([assignments, canAssign]) => {
            this.objectAssignment = assignments.find(assignment => isForObject(assignment, this.object));
            this.permissions = this.objectAssignment ? this.objectAssignment.assigned_permissions : [];
            this.assignments = this.objectAssignment ? assignments.filter(a => a !== this.objectAssignment) : assignments;
            this.canAssign = canAssign;
            if (canAssign) {
                this.valid.emit(true);
                if (this.objectAssignment) {
                    this.updateAssignment(subjectType, subjectId);
                } else {
                    this.assignment.emit(null);
                }
            } else {
                this.valid.emit(false);
                this.assignment.emit(null);
            }
            this.dirty.emit(false);
        });
    }

    private canAssignSubject(subjectType: PermissionSubjectType, subjectId: string): Observable<boolean> {
        switch (subjectType) {
            case PermissionSubjectType.USER:
                return this.usersService.permissions.pipe(
                    first(),
                    map(userPerms => can(userPerms, Permission.PERMISSIONS))
                );
        }
        return of(false);
    }

    private updateAssignment(subjectType: PermissionSubjectType, subjectId: string) {
        const assignment: PermissionAssignment = {
            subject_type: subjectType,
            subject_id: subjectId || null,
            object_type: this.object.type,
            object_id: this.object.id || null,
            assigned_permissions: this.permissions
        };
        this.assignment.emit(assignment);
    }
}

function isForObject(assignment: PermissionAssignment, object: PermissionAssignmentObject): boolean {
    return assignment.object_type === object.type &&
        (assignment.object_id || null) === (object.id || null);
}
